'use client'

import Link from 'next/link'
import { useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react'
import { IconArrowRight, IconRefresh } from '@tabler/icons-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { GpuCartridge } from './gpu-cartridge'
import { Gauge, Led, Lever, RotaryKnob, Screw } from './lab-controls'
import { labKey, primaryPress } from './lab-styles'
import { useCountUp, useLabPower } from './use-lab-power'

export interface ActivationStep {
  title: string
  description: string
  /** The billing or Terms condition that goes with the step. */
  note?: string
  action?: { label: string; href: string }
}

export interface ActivationGpu {
  id: string
  name: string
  vram: number
  vcpus: number
  ram: number
  /** Every region it is offered in, with the estimated hourly rate there. */
  regions: { id: string; label: string; rate: number; rateText: string; createHref: string }[]
}

interface ActivationLabProps {
  steps: ActivationStep[]
  gpus: ActivationGpu[]
  initialGpuId: string
  preferredRegion: string
  osOptions: { id: string; name: string }[]
  topUps: { value: number; label: string }[]
  currencySymbol: string
  provisioningMinutes: number
  includedDiskGb: number
  estimateNote: string
  intro: string
  /** "Choose a machine" + "Full walkthrough", rendered on the server. */
  actions: ReactNode
  /** The equipment-check console, rendered on the server. */
  requirements: ReactNode
}

/** A CSS animation delay, read by the .lab-step-* / .lab-grow classes. */
const at = (seconds: number, extra?: CSSProperties): CSSProperties =>
  ({ ['--at' as string]: `${seconds}s`, ...extra }) as CSSProperties

const pad = (n: number) => String(n).padStart(2, '0')
const STATUS = ['Wallet terminal', 'GPU bay', 'Start sequence', 'Remote session']
const LAMPS = ['Credit', 'GPU', 'Power', 'Link']

export function ActivationLab({
  steps,
  gpus,
  initialGpuId,
  preferredRegion,
  osOptions,
  topUps,
  currencySymbol,
  provisioningMinutes,
  includedDiskGb,
  estimateNote,
  intro,
  actions,
  requirements,
}: ActivationLabProps) {
  const [stage, setStage] = useState(0)
  // Bumped on every step change or replay, so the screen scene restarts.
  const [run, setRun] = useState(0)
  const [gpuIndex, setGpuIndex] = useState(() => Math.max(0, gpus.findIndex((g) => g.id === initialGpuId)))
  const [regionId, setRegionId] = useState(preferredRegion)
  const [osId, setOsId] = useState(osOptions[0]?.id ?? '')
  const [balance, setBalance] = useState(topUps[1]?.value ?? 25)
  const [lastTopUp, setLastTopUp] = useState(topUps[1]?.label ?? '')
  const [powered, setPowered] = useState(true)
  const sectionRef = useRef<HTMLElement>(null)
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])
  const power = useLabPower(sectionRef)
  const lit = power !== 'armed'

  const gpu = gpus[gpuIndex] ?? gpus[0]
  const regionIndex = Math.max(0, gpu?.regions.findIndex((r) => r.id === regionId) ?? 0)
  const region = gpu?.regions[regionIndex]
  const os = osOptions.find((o) => o.id === osId) ?? osOptions[0]

  const rollingBalance = useCountUp(balance, power === 'on', 900)
  const rollingRate = useCountUp(region?.rate ?? null, power === 'on')

  if (!gpu || !region || !os) return null
  const step = steps[stage]
  const running = stage >= 2 && powered
  const last = steps.length - 1

  const balanceText = `${currencySymbol}${(rollingBalance ?? balance).toFixed(2)}`
  const rateText = rollingRate === null ? region.rateText : `${currencySymbol}${rollingRate.toFixed(2)}`

  const goTo = (next: number, focus = false) => {
    const i = Math.min(last, Math.max(0, next))
    setStage(i)
    setRun((r) => r + 1)
    // Starting is step 3: arriving there (or past it) powers the machine on.
    if (i >= 2) setPowered(true)
    if (focus) tabRefs.current[i]?.focus()
  }

  const onTabKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const moves: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }
    if (e.key in moves) {
      e.preventDefault()
      goTo((stage + moves[e.key] + steps.length) % steps.length, true)
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault()
      goTo(e.key === 'Home' ? 0 : last, true)
    }
  }

  const pickGpu = (i: number) => {
    const next = gpus[i]
    if (!next) return
    setGpuIndex(i)
    if (!next.regions.some((r) => r.id === regionId)) {
      setRegionId((next.regions.find((r) => r.id === preferredRegion) ?? next.regions[0]).id)
    }
  }

  const replay = () => setRun((r) => r + 1)

  // Gauge readings for the demo machine.
  const powerLevel = running ? 100 : stage === 1 ? 15 : 0
  const fanLevel = running ? (stage === 2 ? 80 : 55) : 0
  const linkLevel = stage === 3 && powered ? 100 : 0
  const lamps = [stage >= 0, stage >= 1, running, stage === 3 && powered]

  const sideFan = (
    <div className="grid size-20 place-items-center sm:size-24">
      <div className="lab-fan-housing">
        <div className={cn('lab-fan', running && lit && 'is-spinning')} />
      </div>
    </div>
  )

  return (
    <section
      ref={sectionRef}
      aria-labelledby="procedure-title"
      data-power={power}
      className="lab-room relative isolate overflow-hidden"
    >
      <div aria-hidden className="lab-texture pointer-events-none absolute inset-0 -z-10" />
      <div aria-hidden className="lab-lightpool pointer-events-none absolute -right-[15%] top-[12%] -z-10 h-[60%] w-[85%] lg:w-[60%]" />

      <div className="mx-auto w-full max-w-[1320px] px-5 py-14 md:px-8 lg:py-20">
        {/* Heading */}
        <div className="lab-power-item flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="lab-chip inline-flex items-center gap-2.5 px-3 py-1.5 text-sm font-bold uppercase tracking-[0.12em] text-background">
              <Led on={lit} className="lab-blink" />
              Activation sequence
            </p>
            <h2
              id="procedure-title"
              className="mt-5 font-heading text-[clamp(2.5rem,5.5vw,4.75rem)] font-bold uppercase leading-[0.9] tracking-[-0.04em] text-background"
            >
              From sign-up to desktop <span className="lab-glow-text text-accent">in four steps.</span>
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-background/75">{intro}</p>
          </div>
          <div className="flex shrink-0 flex-col gap-4 sm:flex-row">{actions}</div>
        </div>

        {/* Step switches */}
        <div
          role="tablist"
          aria-label="Steps"
          onKeyDown={onTabKey}
          className="lab-power-item mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4"
          style={{ transitionDelay: '80ms' }}
        >
          {steps.map((s, i) => {
            const selected = i === stage
            return (
              <button
                key={s.title}
                ref={(el) => {
                  tabRefs.current[i] = el
                }}
                id={`activation-tab-${i}`}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-controls="activation-panel"
                tabIndex={selected ? 0 : -1}
                onClick={() => goTo(i)}
                className={cn(
                  labKey,
                  'flex min-h-24 flex-col justify-between gap-3 p-4 text-left aria-selected:bg-accent aria-selected:bg-none aria-selected:text-accent-foreground aria-selected:hover:text-accent-foreground aria-selected:shadow-[0_4px_0_0_color-mix(in_oklab,var(--primary)_45%,var(--foreground)),0_0_26px_color-mix(in_oklab,var(--accent)_50%,transparent)]'
                )}
              >
                <span className="flex items-start justify-between gap-2">
                  <span className="font-heading text-4xl font-bold leading-none tabular-nums sm:text-5xl">{pad(i + 1)}</span>
                  {!selected && <Led on={lit && i < stage} className="mt-1" />}
                </span>
                <span className="text-sm font-bold uppercase leading-tight tracking-[0.08em]">{s.title}</span>
              </button>
            )
          })}
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,8fr)_minmax(0,4fr)]">
          {/* Workstation */}
          <div className="lab-power-item lab-plate relative p-3 sm:p-5" style={{ transitionDelay: '140ms' }}>
            <Screw className="left-2 top-2" angle={25} />
            <Screw className="right-2 top-2" angle={100} />
            <Screw className="bottom-2 left-2" angle={160} />
            <Screw className="bottom-2 right-2" angle={60} />

            <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 px-2 pb-3 pt-1">
              <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.12em] text-background">
                <Led on={lit} className={lit ? 'lab-blink' : undefined} />
                Workstation · {STATUS[stage]}
              </p>
              <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-background/70" aria-label="Machine lamps">
                {LAMPS.map((lamp, i) => (
                  <li key={lamp} className="flex items-center gap-1.5">
                    <Led on={lit && lamps[i]} />
                    {lamp}
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid gap-3 lg:grid-cols-[auto_minmax(0,1fr)_auto]">
              {/* Left tower */}
              <div className="lab-well hidden flex-col items-center justify-around gap-2 p-3 lg:flex">
                {sideFan}
                <div className="w-24">
                  <Gauge label="Power" value={powerLevel} max={100} unit="%" on={lit} />
                </div>
              </div>

              {/* Screen */}
              <div className="lab-crt aspect-[4/3] min-h-[340px] w-full sm:aspect-[16/10] sm:min-h-[320px]">
                <div key={`${stage}-${run}`} className="lab-scene-in absolute inset-0">
                  {stage === 0 && <WalletScene balanceText={balanceText} lastTopUp={lastTopUp} />}
                  {stage === 1 && (
                    <CreateScene
                      gpu={gpu}
                      regionLabel={region.label}
                      osName={os.name}
                      rateText={rateText}
                      includedDiskGb={includedDiskGb}
                      estimateNote={estimateNote}
                    />
                  )}
                  {stage === 2 && (
                    <BootScene
                      gpuName={gpu.name}
                      regionLabel={region.label}
                      powered={powered}
                      provisioningMinutes={provisioningMinutes}
                      includedDiskGb={includedDiskGb}
                    />
                  )}
                  {stage === 3 && <DesktopScene gpu={gpu} regionLabel={region.label} osName={os.name} />}
                </div>
              </div>

              {/* Right tower */}
              <div className="lab-well hidden flex-col items-center justify-around gap-2 p-3 lg:flex">
                {sideFan}
                <div className="w-24">
                  <Gauge label="Fans" value={fanLevel} max={100} unit="%" on={lit} />
                </div>
                <div className="w-24">
                  <Gauge label="Link" value={linkLevel} max={100} unit="%" on={lit} />
                </div>
              </div>
            </div>

            {/* Gauges for small screens, where the towers fold away */}
            <div className="lab-well mt-3 grid grid-cols-[auto_repeat(3,minmax(0,1fr))] items-center gap-2 p-2 lg:hidden">
              <div className="grid size-16 place-items-center">
                <div className="lab-fan-housing">
                  <div className={cn('lab-fan', running && lit && 'is-spinning')} />
                </div>
              </div>
              <Gauge label="Power" value={powerLevel} max={100} unit="%" on={lit} />
              <Gauge label="Fans" value={fanLevel} max={100} unit="%" on={lit} />
              <Gauge label="Link" value={linkLevel} max={100} unit="%" on={lit} />
            </div>

            {/* Control deck */}
            <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,15rem)_minmax(0,1fr)]">
              {/* GPU bay */}
              <div className="lab-well relative flex min-h-36 items-center justify-center overflow-hidden p-3">
                <p className="absolute left-3 top-2 text-xs font-bold uppercase tracking-[0.12em] text-background/60">GPU bay</p>
                {stage >= 1 ? (
                  <div key={gpu.id} className="lab-slot-in mt-3">
                    <GpuCartridge
                      code={pad(gpuIndex + 1)}
                      name={gpu.name}
                      vram={gpu.vram}
                      vcpus={gpu.vcpus}
                      rank={gpuIndex + 1}
                      lit={lit && running}
                    />
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-background/50">Empty. A GPU slots in at step 2.</p>
                )}
                <div aria-hidden className="lab-rail absolute inset-x-3 bottom-3 h-1.5" />
              </div>

              {/* Controls for the current step */}
              <div className="lab-well flex flex-wrap items-center gap-4 p-3 sm:p-4">
                {stage === 0 && (
                  <div className="w-full">
                    <p className="text-sm font-bold uppercase tracking-[0.12em] text-background">Add demo credit</p>
                    <div className="mt-2 grid grid-cols-4 gap-2">
                      {topUps.map((amount) => (
                        <button
                          key={amount.value}
                          type="button"
                          onClick={() => {
                            setBalance((b) => Math.min(999, b + amount.value))
                            setLastTopUp(amount.label)
                            replay()
                          }}
                          className={cn(labKey, 'h-12 text-base font-bold tabular-nums')}
                        >
                          +{amount.label}
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 flex items-center justify-between gap-3 text-sm text-background/60">
                      <span>A demo: nothing is charged here.</span>
                      <button
                        type="button"
                        onClick={() => {
                          setBalance(0)
                          setLastTopUp('')
                        }}
                        className="shrink-0 font-bold text-background underline decoration-2 underline-offset-4 hover:text-accent"
                      >
                        Clear
                      </button>
                    </p>
                  </div>
                )}

                {stage === 1 && (
                  <>
                    <RotaryKnob
                      label="GPU"
                      value={gpuIndex}
                      min={0}
                      max={gpus.length - 1}
                      wrap
                      valueText={gpu.name}
                      onChange={pickGpu}
                    />
                    <RotaryKnob
                      label="Region"
                      value={regionIndex}
                      min={0}
                      max={Math.max(1, gpu.regions.length - 1)}
                      wrap
                      valueText={region.label}
                      onChange={(v) => setRegionId((gpu.regions[v] ?? gpu.regions[0]).id)}
                    />
                    <div className="flex min-w-0 flex-1 flex-col gap-2">
                      <p className="text-sm font-bold uppercase tracking-[0.12em] text-background">System</p>
                      <div role="group" aria-label="Operating system" className="grid gap-2 sm:grid-cols-2">
                        {osOptions.map((option) => (
                          <button
                            key={option.id}
                            type="button"
                            aria-pressed={option.id === os.id}
                            onClick={() => setOsId(option.id)}
                            className={cn(labKey, 'min-h-11 px-3 py-2 text-left text-sm font-bold leading-tight')}
                          >
                            {option.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {stage === 2 && (
                  <>
                    <Lever
                      label="Power"
                      on={powered}
                      offLabel="Stop"
                      onLabel="Start"
                      onToggle={() => {
                        setPowered((p) => !p)
                        replay()
                      }}
                    />
                    <p className="min-w-0 flex-1 text-sm leading-relaxed text-background/75">
                      Throw the lever to start or stop the demo machine. Billing runs from the start, boot time included, until
                      the stop.
                    </p>
                    {powered && (
                      <button type="button" onClick={replay} className={cn(labKey, 'inline-flex h-11 items-center gap-2 px-4 text-sm font-bold uppercase')}>
                        <IconRefresh className="size-4" aria-hidden />
                        Replay
                      </button>
                    )}
                  </>
                )}

                {stage === 3 && (
                  <>
                    <button type="button" onClick={replay} className={cn(labKey, 'inline-flex h-12 items-center gap-2 px-4 text-sm font-bold uppercase')}>
                      <IconRefresh className="size-4" aria-hidden />
                      Reconnect
                    </button>
                    <p className="min-w-0 flex-1 text-sm leading-relaxed text-background/75">
                      Connect from Windows, macOS, Linux, Android or iOS with a Remote Desktop app.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Step panel */}
          <div
            id="activation-panel"
            role="tabpanel"
            aria-labelledby={`activation-tab-${stage}`}
            className="lab-power-item lab-plate relative flex flex-col gap-4 p-5 sm:p-6"
            style={{ transitionDelay: '200ms' }}
          >
            <Screw className="left-2 top-2" angle={70} />
            <Screw className="right-2 top-2" angle={15} />
            <Screw className="bottom-2 left-2" angle={110} />
            <Screw className="bottom-2 right-2" angle={150} />

            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-bold uppercase tracking-[0.12em] text-background/70">
                Step {pad(stage + 1)} of {pad(steps.length)}
              </p>
              <Led on={lit} className="lab-blink" />
            </div>

            <div key={`copy-${stage}`} className="lab-step-in flex flex-col gap-4">
              <div className="flex items-end gap-4">
                <span className="lab-glow-text font-heading text-7xl font-bold leading-[0.8] tabular-nums text-accent">{pad(stage + 1)}</span>
                <h3 className="font-heading text-3xl font-bold uppercase leading-[0.95] tracking-tight text-background">{step.title}</h3>
              </div>
              <p className="text-base leading-relaxed text-background/85">{step.description}</p>
              {step.note && (
                <div className="lab-well flex gap-3 p-3">
                  <Led on={lit} className="mt-1.5" />
                  <p className="text-sm leading-relaxed text-background/75">{step.note}</p>
                </div>
              )}
              {stage === 1 && (
                <div className="lab-lcd px-4 py-3">
                  <p className="text-xs font-bold uppercase tracking-[0.1em] opacity-80">Estimated rate · {gpu.name}</p>
                  <p className="lab-glow-text mt-1 text-3xl font-bold tabular-nums">
                    {rateText}
                    <span className="ml-1 text-base">/hr</span>
                  </p>
                  <p className="mt-1 text-xs opacity-75">{region.label}</p>
                </div>
              )}
              {(step.action || stage === 1) && (
                <Link
                  href={stage === 1 ? region.createHref : step.action!.href}
                  className="inline-flex items-center gap-2 self-start text-sm font-bold uppercase tracking-[0.12em] text-background underline decoration-accent decoration-2 underline-offset-4 hover:text-accent"
                >
                  {stage === 1 ? 'Open the configurator with this' : step.action!.label}
                  <IconArrowRight className="size-4" aria-hidden />
                </Link>
              )}
            </div>

            <div className="mt-auto flex flex-col gap-4 pt-2">
              <div>
                <div
                  role="progressbar"
                  aria-label="Progress through the steps"
                  aria-valuemin={1}
                  aria-valuemax={steps.length}
                  aria-valuenow={stage + 1}
                  aria-valuetext={`Step ${stage + 1} of ${steps.length}: ${step.title}`}
                  className="grid gap-1.5"
                  style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}
                >
                  {steps.map((s, i) => (
                    <span
                      key={s.title}
                      className={cn(
                        'h-2.5 transition-[background-color,box-shadow] duration-500',
                        i <= stage ? 'bg-accent shadow-[0_0_10px_color-mix(in_oklab,var(--accent)_60%,transparent)]' : 'bg-background/15'
                      )}
                    />
                  ))}
                </div>
              </div>
              <Button className={cn(primaryPress, 'w-full')} onClick={() => goTo(stage === last ? 0 : stage + 1)}>
                {stage === last ? 'Back to step 01' : `Next Step: ${steps[stage + 1].title}`}
                <IconArrowRight className="size-4" aria-hidden />
              </Button>
            </div>
          </div>
        </div>

        {requirements}
      </div>
    </section>
  )
}

/* ---------- Screen scenes. Each rests on its last frame when motion is reduced. ---------- */

function ScreenLabel({ children }: { children: ReactNode }) {
  return <p className="text-xs font-bold uppercase tracking-[0.14em] text-accent sm:text-sm">{children}</p>
}

function WalletScene({ balanceText, lastTopUp }: { balanceText: string; lastTopUp: string }) {
  return (
    <div className="absolute inset-0 grid grid-cols-[minmax(0,1fr)_auto] gap-4 p-5 sm:gap-8 sm:p-8">
      <div className="flex min-w-0 flex-col justify-between gap-4">
        <ScreenLabel>Wallet terminal</ScreenLabel>
        <div>
          <p className="text-sm text-background/65">Balance</p>
          <p className="lab-glow-text font-heading text-4xl font-bold leading-none tabular-nums text-accent sm:text-7xl">{balanceText}</p>
          {lastTopUp && (
            <p className="lab-step-in mt-3 text-sm text-background/80 sm:text-base" style={at(0.9)}>
              + {lastTopUp} added by card
            </p>
          )}
        </div>
        <ul className="flex flex-wrap gap-2 text-sm">
          {['Card payment', 'Prepaid wallet', 'No subscription'].map((chip, i) => (
            <li key={chip} className="lab-step-in lab-chip px-2.5 py-1 text-background" style={at(0.3 + i * 0.12)}>
              {chip}
            </li>
          ))}
        </ul>
      </div>

      {/* Card reader */}
      <div aria-hidden className="flex h-full w-24 flex-col items-center sm:w-36">
        <div className="relative h-[46%] w-full overflow-hidden">
          <div className="lab-card lab-card-in absolute inset-x-[12%] bottom-0 h-[78%]">
            <div className="lab-card-chip absolute left-[14%] top-[44%] h-[18%] w-[22%]" />
          </div>
        </div>
        <div className="lab-slot h-2 w-full" />
        <div className="lab-plate mt-1 flex w-full flex-1 flex-col items-center justify-center gap-2 p-2">
          <div className="grid w-[70%] grid-cols-3 gap-1">
            {Array.from({ length: 9 }, (_, i) => (
              <span key={i} className="aspect-square bg-background/15" />
            ))}
          </div>
          <span className="lab-step-in flex items-center gap-1.5 text-xs font-bold uppercase text-accent" style={at(0.9)}>
            <Led on />
            Paid
          </span>
        </div>
      </div>
    </div>
  )
}

function CreateScene({
  gpu,
  regionLabel,
  osName,
  rateText,
  includedDiskGb,
  estimateNote,
}: {
  gpu: ActivationGpu
  regionLabel: string
  osName: string
  rateText: string
  includedDiskGb: number
  estimateNote: string
}) {
  const rows = [
    { label: 'GPU', value: `${gpu.name} · ${gpu.vram} GB`, key: gpu.id },
    { label: 'CPU / RAM', value: `${gpu.vcpus} vCPU · ${gpu.ram} GB`, key: `${gpu.id}-cpu` },
    { label: 'Region', value: regionLabel, key: regionLabel },
    { label: 'System', value: osName, key: osName },
    { label: 'Disk', value: `${includedDiskGb} GB included`, key: 'disk' },
  ]
  return (
    <div className="absolute inset-0 flex flex-col justify-between gap-3 p-5 sm:p-8">
      <ScreenLabel>Machine configurator</ScreenLabel>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:gap-x-6">
        {rows.map((row, i) => (
          <div key={row.label} className="min-w-0">
            <dt className="text-xs uppercase tracking-[0.12em] text-background/55 sm:text-sm">{row.label}</dt>
            <dd key={row.key} className="lab-step-in truncate text-base font-bold text-background sm:text-lg" style={at(i * 0.08)}>
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
      <div>
        <p className="lab-glow-text font-heading text-5xl font-bold leading-none tabular-nums text-accent sm:text-6xl">
          {rateText}
          <span className="ml-1 text-xl">/hr</span>
        </p>
        <p className="mt-2 text-sm text-background/65">Estimated. {estimateNote}</p>
      </div>
    </div>
  )
}

function BootScene({
  gpuName,
  regionLabel,
  powered,
  provisioningMinutes,
  includedDiskGb,
}: {
  gpuName: string
  regionLabel: string
  powered: boolean
  provisioningMinutes: number
  includedDiskGb: number
}) {
  if (!powered) {
    return (
      <div className="absolute inset-0 flex flex-col justify-between p-5 sm:p-8">
        <ScreenLabel>Start sequence</ScreenLabel>
        <div>
          <p className="font-heading text-4xl font-bold uppercase leading-none text-background/80 sm:text-5xl">Stopped</p>
          <p className="mt-3 max-w-md text-sm text-background/65 sm:text-base">
            Charges stop when the machine is stopped. Its disk is kept, so you can start it again later.
          </p>
        </div>
        <p className="text-sm text-background/50">Throw the power lever to start it.</p>
      </div>
    )
  }

  const log = [
    { t: 0.2, text: 'Start requested. Charges accrue from here.' },
    { t: 0.9, text: `${gpuName} assigned in ${regionLabel}` },
    { t: 1.6, text: `${includedDiskGb} GB disk attached` },
    { t: 2.3, text: 'Windows is booting' },
    { t: 3.0, text: 'Address and password ready on the machine page' },
  ]
  return (
    <div className="absolute inset-0 flex flex-col gap-4 p-5 sm:p-8">
      <div className="flex items-center justify-between gap-3">
        <ScreenLabel>Start sequence</ScreenLabel>
        <span className="grid text-sm font-bold uppercase tracking-[0.12em] sm:text-base [&>*]:col-start-1 [&>*]:row-start-1">
          <span className="lab-step-out text-background" style={at(3.2)}>
            Starting…
          </span>
          <span className="lab-step-in flex items-center gap-2 text-accent" style={at(3.2)}>
            <Led on />
            Running
          </span>
        </span>
      </div>
      <div className="h-2.5 bg-background/15">
        <div
          className="lab-grow h-full bg-accent shadow-[0_0_12px_var(--accent)]"
          style={at(0.1, { ['--dur' as string]: '3.1s' } as CSSProperties)}
        />
      </div>
      <ol className="flex flex-1 flex-col gap-1.5 text-sm text-background/80 sm:text-base">
        {log.map((line) => (
          <li key={line.text} className="lab-step-in flex gap-3" style={at(line.t)}>
            <span aria-hidden className="text-accent">&gt;</span>
            {line.text}
          </li>
        ))}
      </ol>
      <p className="text-sm text-background/55">
        Sped up for the demo. A real machine is usually ready within {provisioningMinutes} minutes.
      </p>
    </div>
  )
}

function DesktopScene({ gpu, regionLabel, osName }: { gpu: ActivationGpu; regionLabel: string; osName: string }) {
  return (
    <div className="lab-desktop absolute inset-0 overflow-hidden">
      {/* Desktop icons */}
      <ul aria-hidden className="absolute left-3 top-3 flex flex-col gap-3 sm:left-5 sm:top-5">
        {['This PC', 'Files', 'Recycle Bin'].map((icon) => (
          <li key={icon} className="flex w-16 flex-col items-center gap-1 text-center text-xs text-background">
            <span className="size-8 bg-background/80 shadow-[inset_0_-6px_0_0_color-mix(in_oklab,var(--accent)_60%,var(--background))]" />
            {icon}
          </li>
        ))}
      </ul>

      {/* The session window, once connected */}
      <div className="lab-step-in lab-window absolute right-3 top-3 w-[min(19rem,62%)] sm:right-6 sm:top-6" style={at(2.5)}>
        <div className="flex items-center justify-between bg-foreground px-3 py-1.5 text-xs font-bold text-background">
          <span>System</span>
          <span aria-hidden className="flex gap-1">
            <span className="size-2 bg-background/40" />
            <span className="size-2 bg-background/40" />
            <span className="size-2 bg-accent" />
          </span>
        </div>
        <dl className="grid gap-1 p-3 text-sm">
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">GPU</dt>
            <dd className="font-bold">{gpu.name}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Memory</dt>
            <dd className="font-bold">{gpu.ram} GB</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Windows</dt>
            <dd className="truncate font-bold">{osName}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Region</dt>
            <dd className="truncate font-bold">{regionLabel}</dd>
          </div>
        </dl>
      </div>

      {/* Remote Desktop dialog: connects, then gets out of the way */}
      <div className="lab-rdp-out absolute inset-0 grid place-items-center p-4" style={at(2.2)}>
        <div className="lab-window w-[min(22rem,92%)]">
          <div className="bg-foreground px-3 py-1.5 text-xs font-bold text-background">Remote Desktop Connection</div>
          <div className="grid gap-2 p-4 text-sm">
            <p className="flex justify-between gap-3">
              <span className="text-muted-foreground">Computer</span>
              <span className="font-bold tabular-nums">203.0.113.24 (example)</span>
            </p>
            <p className="flex justify-between gap-3">
              <span className="text-muted-foreground">Password</span>
              <span className="font-bold tracking-[0.2em]">••••••••</span>
            </p>
            <div className="mt-1 h-2 bg-foreground/15">
              <div className="lab-grow h-full bg-primary" style={at(0.2, { ['--dur' as string]: '1.6s' } as CSSProperties)} />
            </div>
            <p className="grid font-bold [&>*]:col-start-1 [&>*]:row-start-1">
              <span className="lab-step-out" style={at(1.8)}>
                Connecting…
              </span>
              <span className="lab-step-in text-primary" style={at(1.8)}>
                Connected
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Taskbar */}
      <div className="lab-taskbar absolute inset-x-0 bottom-0 flex h-10 items-center justify-between gap-3 px-3">
        <span aria-hidden className="flex items-center gap-2">
          <span className="grid size-5 grid-cols-2 gap-0.5">
            <span className="bg-accent" />
            <span className="bg-accent" />
            <span className="bg-accent" />
            <span className="bg-accent" />
          </span>
          <span className="hidden h-6 w-32 bg-background/15 sm:block" />
          <span className="size-5 bg-background/30" />
          <span className="size-5 bg-background/30" />
        </span>
        <span className="lab-step-in flex items-center gap-2 text-xs font-bold uppercase tracking-[0.1em] text-background" style={at(2.2)}>
          <Led on />
          Remote Desktop · connected
        </span>
      </div>
    </div>
  )
}
